import React, { useState, useEffect } from "react";
import { Table, Empty } from "antd";
import { useMediaQuery } from "react-responsive";

const CustomTable = ({
    columns,
    dataSource = [],
    defaultPageSize = 20,
    pageSizeOptions = ["2", "10", "20", "50"],
    style,
    onPaginationChange,
    total,
    loading = false,
    emptyMessage = "No data available",
    pagination = true, // New prop with default value set to true
}) => {
    // Ensure dataSource is always an array
    const safeDataSource = Array.isArray(dataSource) ? dataSource : [];

    const isMobile = useMediaQuery({ maxWidth: 768 }); // Detect mobile screens
    const isTablet = useMediaQuery({ minWidth: 769, maxWidth: 1024 }); // Detect tablet screens

    const [paginationState, setPagination] = useState({
        pageSize: defaultPageSize,
        current: 1,
    });

    // Adjust default page size for mobile and tablet
    useEffect(() => {
        if (isMobile) {
            setPagination((prev) => ({
                ...prev,
                pageSize: 5, // Smaller page size for mobile
            }));
        } else if (isTablet) {
            setPagination((prev) => ({
                ...prev,
                pageSize: 15, // Medium page size for tablet
            }));
        }
    }, [isMobile, isTablet]);

    const handleTableChange = (paginationConfig) => {
        const newPagination = {
            pageSize: paginationConfig.pageSize,
            current: paginationConfig.current,
        };
        setPagination(newPagination);

        // Notify the parent to fetch new data based on the updated pagination
        if (onPaginationChange) {
            onPaginationChange(newPagination);
        }
    };

    return (
        <div
            style={{
                overflowX: (isMobile || isTablet) ? "auto" : "initial", // Horizontal scroll on mobile and tablet
            }}
        >
            <Table
                columns={columns.map((col) => ({
                    ...col,
                    responsive: col.responsive || undefined, // Use responsive property for column visibility
                    ellipsis: (isMobile || isTablet) ? true : col.ellipsis, // Enable ellipsis on mobile/tablet
                }))}
                dataSource={safeDataSource}
                size={isMobile ? "small" : isTablet ? "small" : "default"}
                bordered
                rowKey={(record) => record._id || record.key}
                scroll={{ x: (isMobile || isTablet) ? 'max-content' : undefined }}
                pagination={
                    pagination
                        ? {
                            current: paginationState.current,
                            pageSize: paginationState.pageSize,
                            showSizeChanger: !isMobile, // Hide size changer on mobile
                            pageSizeOptions: isMobile ? ["5", "10"] : pageSizeOptions,
                            showQuickJumper: !isMobile, // Hide quick jumper on mobile
                            total: total, // Pass the total number of items here
                            size: isMobile ? "small" : "default",
                        }
                        : false // Disable pagination if `pagination` prop is false
                }
                onChange={handleTableChange}
                loading={loading}
                locale={{
                    emptyText: <Empty description={emptyMessage} />,
                }}
                style={style}
            />
        </div>
    );
};

CustomTable.defaultProps = {
    columns: [],
    dataSource: [],
    defaultPageSize: 20,
    pageSizeOptions: ["2", "10", "20", "50"],
    style: {},
    onPaginationChange: null,
    loading: false,
    emptyMessage: "No data available",
    pagination: true, // Default value is true, meaning pagination will show
};

export default CustomTable;
